import React from 'react';
import classNames from 'classnames';
import { get, memoize, difference } from 'lodash';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import Checkbox from '@atlaskit/checkbox';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CheckCircle from '@atlaskit/icon/core/check-circle';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Tooltip from '@atlaskit/tooltip';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { isAccessProduct } from '@atlassian/cssxp-utils-product';
import { STATUSPAGE_ADDON_PRODUCT_ID } from '@css-xp/ppc-constants';
import { EntitlementIcon } from '@css-xp/base';
import { NoData } from '@css-xp/ppc-common';
import { isStatusPageAddOnProduct } from '@css-xp/ppc-utils';
import { TABLE_HEADER } from './constants/table';
import './products-table-items.scss';

export const InactiveReasonStrings = {
  INVALID_ADD_ON: 'Selected JSM offering ID is not eligible for Add-ons order.',
};

interface Props {
  products: PartnerProductCatalogueItem[];
  quoteItems: PartnerProductCatalogueItem[];
  setCatalogueItem: (item: PartnerProductCatalogueItem) => void;
  setIsPartnerCatalogueInformationLoading: (flag: boolean) => void;
  txaId?: string;
  setEstimatesToBeRefreshed: (flag: boolean) => void;
  isRowClickable?: boolean;
  existingCloudSiteProductIds?: string[];
  allowedParentProductIdsForAppsOrder?: string[];
  catalogExcludedProducts?: string[];
  isSandboxCloudsite?: boolean;
  showCheckbox?: boolean;
  checkedProductsList?: PartnerProductCatalogueItem[];
  setCheckedProductsList?: (checkedProductsList: PartnerProductCatalogueItem[]) => void;
  parentContainerOrProductId?: string | null;
  isDesignIteration1Enabled?: boolean;
}

export const createHead = memoize((showCheckbox?: boolean, isAppificationEnabled?: boolean) => {
  if (showCheckbox) {
    return [
      { name: '', content: 'Select', customClassNames: 'col-10' },
      {
        name: isAppificationEnabled ? TABLE_HEADER.APPS : TABLE_HEADER.PRODUCT,
        content: TABLE_HEADER.PRODUCT,
        customClassNames: 'col-60',
      },
      { name: TABLE_HEADER.PARTNER_DISCOUNT, content: TABLE_HEADER.PARTNER_DISCOUNT, customClassNames: 'col-30' },
    ];
  }
  return [
    { name: TABLE_HEADER.PRODUCT, content: TABLE_HEADER.PRODUCT, customClassNames: 'col-50' },
    { name: TABLE_HEADER.PARTNER_DISCOUNT, content: TABLE_HEADER.PARTNER_DISCOUNT, customClassNames: 'col-30' },
    { name: '', content: 'Select', customClassNames: 'col-20' },
  ];
});

export type ProductCatalogueItem = PartnerProductCatalogueItem & {
  hasOptedInForPartnerPromotion?: boolean;
  vendorName?: string | null;
  isProduct?: boolean;
  isAddOn?: boolean;
};

export const createRows = ({
  products,
  quoteItems,
  setCatalogueItem,
  setIsPartnerCatalogueInformationLoading,
  setEstimatesToBeRefreshed,
  isRowClickable,
  existingCloudSiteProductIds,
  allowedParentProductIdsForAppsOrder,
  catalogExcludedProducts,
  isSandboxCloudsite,
  showCheckbox,
  checkedProductsList = [],
  setCheckedProductsList,
  parentContainerOrProductId,
  isDesignIteration1Enabled,
}: Props) => {
  const updatedProducts = (products ?? []).filter((product) => !catalogExcludedProducts?.includes(product?.id));

  return updatedProducts.map((product: ProductCatalogueItem, index: number) => {
    const name = product?.name;
    const vendorName = product?.vendorName;
    const productId = product?.id ?? '';
    const isChecked =
      quoteItems?.some((item) => item?.id === product?.id) ||
      quoteItems?.some((item: any) => item?.productId === product?.id); // for non jira products and apps present in the cart.

    const availableParentProduct =
      parentContainerOrProductId ?? product?.offering?.flatMap((item: any) => item?.availableProducts);

    /*
     * This condition is to block apps which are not having cloud site products in available parent product of app
     */
    const isAppAllowedToOrder = product?.isApp
      ? !!allowedParentProductIdsForAppsOrder?.filter((value) => availableParentProduct?.includes(value)).length
      : true;

    // This condition is to block existing cloudsite subscription products. if product is already there in cloudsite subscriptions then we should not allow again to add product
    const isProductNotInCloudSiteSubscription = !existingCloudSiteProductIds?.includes(product.id);

    // This condition is to block all products for sandbox cloudsite
    const isSandboxCloudsiteAndEligibleOrderingProduct = isSandboxCloudsite && product?.isProduct;

    const isAddonDisableForOrder = !product?.isEnabledForOrder && product?.isAddOn;

    // few products are not app and also not enabled for order
    const isEnabledForOrder =
      (product?.isEnabledForOrder || product?.isApp) &&
      isRowClickable &&
      isProductNotInCloudSiteSubscription &&
      isAppAllowedToOrder &&
      !isAccessProduct(product?.id) &&
      !isSandboxCloudsiteAndEligibleOrderingProduct;

    const handleAddProduct = () => {
      if (!isChecked) {
        setIsPartnerCatalogueInformationLoading(true);
      }
      setCatalogueItem({ ...product });
      setEstimatesToBeRefreshed(true);
    };

    const handleCheckboxChange = () => {
      const isChecked = checkedProductsList?.some((item) => item?.id === product?.id);
      if (isChecked) {
        setCheckedProductsList?.(difference(checkedProductsList, [product]));
      } else {
        setCheckedProductsList?.([...checkedProductsList, product]);
      }
    };

    const inactiveReason = (() => {
      if (isAddonDisableForOrder) {
        return InactiveReasonStrings.INVALID_ADD_ON;
      }
      return;
    })();

    const rowData = createHead(showCheckbox).map((column) => {
      switch (column.content) {
        case 'Select':
          return showCheckbox ? (
            <td className="select-product-checkbox">
              <Tooltip content={inactiveReason} position="bottom">
                <Checkbox
                  isChecked={isChecked || checkedProductsList?.some((item) => item?.id === product?.id)}
                  onChange={() => handleCheckboxChange()}
                  label={''}
                  value="Controlled Checkbox"
                  name="controlled-checkbox"
                  isDisabled={isChecked || !isEnabledForOrder}
                />
              </Tooltip>
            </td>
          ) : (
            <td>
              {isEnabledForOrder ? (
                <Button
                  testId={'product-add-remove-btn'}
                  className={'add-remove-cta-btn'}
                  onClick={handleAddProduct}
                  iconBefore={isChecked ? <CrossIcon label="" size="small" /> : <AddIcon label="" size="small" />}
                />
              ) : (
                <Tooltip
                  position={'top'}
                  content={'You can’t place an order for this item'}
                  testId="ppc-checkout-ordering-disabled-info"
                >
                  <Button
                    testId={'product-add-remove-btn'}
                    className={'add-remove-cta-btn-disable'}
                    isDisabled={true}
                    iconBefore={<CrossIcon label="" size="small" />}
                  />
                </Tooltip>
              )}
            </td>
          );

        case TABLE_HEADER.PRODUCT:
          return (
            <td
              className="catalog-table-product"
              onClick={() => isEnabledForOrder && (showCheckbox ? handleCheckboxChange() : handleAddProduct())}
            >
              <EntitlementIcon
                productId={
                  // this is required for statuspage addons to show correct icon
                  isStatusPageAddOnProduct(productId) && product?.isAddOn ? STATUSPAGE_ADDON_PRODUCT_ID : productId
                }
                className="avatar medium"
                isCloud={true}
                logoId={product?.logoId}
              />
              <div className="catalog-table-product-detail">
                <div className="catalog-table-product-detail-name">{name}</div>
                <div className="catalog-table-product-detail-vendorName">{vendorName}</div>
              </div>
            </td>
          );
        case TABLE_HEADER.PARTNER_DISCOUNT:
          return (
            <td onClick={() => isEnabledForOrder && (showCheckbox ? handleCheckboxChange() : handleAddProduct())}>
              {product?.hasOptedInForPartnerPromotion ? (
                showCheckbox ? (
                  <Box paddingInlineStart="space.400">
                    <CheckCircle label="" color={token('color.icon')} />
                  </Box>
                ) : (
                  <div data-testid="partner-promotion-check">
                    {/* FIX ME: token was not taking correct color value */}
                    <CheckCircleIcon size="medium" label="" primaryColor={'#626F86'} />
                  </div>
                )
              ) : isDesignIteration1Enabled ? (
                <span>N/A</span>
              ) : (
                <NoData />
              )}
            </td>
          );
      }
    });
    return (
      <tr
        key={`row-${index}-${name}`}
        data-testId={`row-${index}-${product?.id}`}
        className={classNames({ 'clickable-row': isEnabledForOrder })}
      >
        {rowData}
      </tr>
    );
  });
};
